import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { CountryCode } from 'libphonenumber-js';
import { Logger } from '../../../../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../../../../infrastructure/context/user-context-provider';
import { OrganizationService } from '../../../../services/organizations.service';
import { formatPhoneNumber } from '../../../../../../utilities/PhoneUtility';

export interface DialogData {
  org: any
}

@Component({
  selector: 'app-org-basic-info-dialog',
  templateUrl: './org-basic-info-dialog.component.html',
  styleUrls: ['./org-basic-info-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    MatOptionModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIcon,
    MatExpansionModule
  ]
})
export class OrgBasicInfoDialogComponent implements OnInit {

  safelyServices: any[] = [
    { id: 0, name: 'Guest Verification and Insurance Protection' },
    { id: 1, name: 'Guest Verification Only' },
    { id: 2, name: 'Insurance Protection Only' }
  ]

  saving: boolean = false;
  form!: FormGroup;
  organizationId?: string | null;
  safely_service_id: number = 0;

  constructor(
    public dialogRef: MatDialogRef<OrgBasicInfoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private orgService: OrganizationService,
    private formBuilder: FormBuilder,
    public userContextProvider: UserContextProvider,
    private logger: Logger
  ) { }

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    if (this.data.org.for_reservation && this.data.org.for_guest_verification) {
      this.safely_service_id = 0;
    }
    if (!this.data.org.for_reservation && this.data.org.for_guest_verification) {
      this.safely_service_id = 1
    }
    if (this.data.org.for_reservation && !this.data.org.for_guest_verification) {
      this.safely_service_id = 2;
    }

    this.form = this.formBuilder.group({
      name: [this.data.org.name, [Validators.required, Validators.pattern(/[\S]/)]],
      org_name_to_guest: [this.data.org.org_name_to_guest, Validators.pattern(/[\S]/)],
      website: [this.data.org.website, [Validators.required, Validators.pattern(/[\S]/)]],
      logo_url: [this.data.org.logo?.photo_url, Validators.pattern(/[\S]/)],
      contact_first_name: [this.data.org.contact_first_name, [Validators.required, Validators.pattern(/[\S]/)]],
      contact_last_name: [this.data.org.contact_last_name, [Validators.required, Validators.pattern(/[\S]/)]],
      email_address: [this.data.org.email_address, [Validators.required, Validators.email]],
      notification_email: [this.data.org.notification_email, [Validators.required, Validators.email]],
      phone_number: [this.data.org.phone_number, [Validators.required, Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)]],
      is_receiving_alert: [this.data.org.is_receiving_alert, Validators.required],
      safely_service_id: [this.safely_service_id, Validators.required],
      send_check_to_safely: [this.data.org.send_check_to_safely],
      notify_guest_for_additional_info: [this.data.org.email_guest_for_details],
      receive_email_on_guest_verification_complete: [this.data.org.notify_guest_verification]
    });
  }

  async onSaveClick() {
    try {
      this.saving = true;

      const organizationPayload = this.buildOrganizationWithUpdatesFromForm();
      this.organizationId = (await this.orgService.updateOrganization(organizationPayload)).id?.toString();
      if (this.organizationId) {
        this.dialogRef.close(organizationPayload);
      } else {
        this.logger.handleError(`Error occurred updating organization ID: ${ organizationPayload.id }`, 'An error occurred while submitting Organization', `Error while submitting Organization ID: ${ organizationPayload.id }`);
      }
    } catch (e) {
      this.logger.handleError(e, 'An error occurred while submitting Organization', 'Error while submitting Organization');
    } finally {
      this.saving = false;
    }
  }

  onPhoneNumberChanged(event: Event) {
    this.updatePhoneNumber((event.target as HTMLInputElement).value);
  }

  updatePhoneNumber(phoneNumber: string) {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber, this.userContextProvider.countryCode as CountryCode);
    this.form.patchValue({ phone_number: formattedPhoneNumber });
  }

  buildOrganizationWithUpdatesFromForm() {
    let updateOrgObj = {
      ...this.data.org,
      ...this.form.value
    };

    if (updateOrgObj.notify_guest_verification) {
      updateOrgObj.notify_guest_verification = 1;
    } else {
      updateOrgObj.notify_guest_verification = 0;
    }

    if (updateOrgObj.is_receiving_alert) {
      updateOrgObj.is_receiving_alert = 1;
    } else {
      updateOrgObj.is_receiving_alert = 0;
    }

    if (this.form.controls['logo_url'].value) {
      updateOrgObj.photo = this.form.controls['logo_url'].value;
    }
    
    if (this.data.org.logo?.photo_id) {
      updateOrgObj.photo_id = this.data.org.logo.photo_id;
    }

    if (this.form.controls['safely_service_id'].value === 0) {
      updateOrgObj.for_reservation = 1;
      updateOrgObj.for_guest_verification = 1;
    }
    if (this.form.controls['safely_service_id'].value === 1) {
      updateOrgObj.for_reservation = 0;
      updateOrgObj.for_guest_verification = 1;
    }
    if (this.form.controls['safely_service_id'].value === 2) {
      updateOrgObj.for_reservation = 1;
      updateOrgObj.for_guest_verification = 0;
    }

    if (this.form.controls['notify_guest_for_additional_info'].value) {
      updateOrgObj.email_guest_for_details = 1;
    } else {
      updateOrgObj.email_guest_for_details = 0;
    }
    if (this.form.controls['receive_email_on_guest_verification_complete'].value) {
      updateOrgObj.notify_guest_verification = 1;
    } else {
      updateOrgObj.notify_guest_verification = 0;
    }

    updateOrgObj.organization_name = this.form.controls['name'].value

    return updateOrgObj;
  }

  get email_address() {
    return this.form.controls['email_address'];
  }

  get notification_email() {
    return this.form.controls['notification_email'];
  }

  get photoUrl() {
    return this.form.controls['logo_url'];
  }
}